import { Section } from '@/components/ANewDesign/BottomModalContent/Content/Bet/BetModalContent/Section'
import { DS } from '@/design-system'
import { usePlayerActions } from '@/hooks/convex/usePlayerActions'
import { useState } from 'react'
import { StyleSheet, View, ViewStyle, TouchableOpacity } from 'react-native'
import { createComponent } from 'react-native-small-ui'
import { translate } from '../../../i18n'
import { ManualEntry } from '@/components/ANewDesign/BottomModalContent/Content/Bet/BetModalContent/ManualEntry'
import { Checkbox } from '@/components/ANewDesign/Checkbox'
import { numberWithCommas } from '../../../utils/numberWithCommas'
import { useGameBottomSheetStore } from '../../../screens/GameContainer/GameBottomSheet/useGameBottomSheetStore'
import { BetHint, isValidBetForPlayer } from '@ppwr/convex/shared/bettingRecommender'
import { ActionType } from '@ppwr/convex/shared/types'
import { useAnimationFrame } from '../hooks/useAnimationFrame'
import { getDSColorWithOpacity } from '../../../design-system/Palette'
import { useGameState } from '@/screens/GameContainer/useGameState'
import { triggerHaptic } from '../../../screens/new-design/utils/triggerHaptics/triggerHaptics'

const Box = createComponent(View, {
  flexGrow: 1
})
const Row = createComponent(View, {
  flexDirection: 'row'
})

type BetControllerProps = {
  containerStyle?: ViewStyle
}

export const BetController = (props: BetControllerProps) => {
  const isSoundEnabled = true
  const closeSheet = useGameBottomSheetStore.getState().closeSheet
  const { game, myPlayer, biggestBet, minimumRaise = 0, playersOnlyCalledBB, currentHandActions } = useGameState()
  const { onBet, onAllIn, onCall } = usePlayerActions(game?._id, myPlayer?.userId as string)
  const [amount, setAmount] = useState('')
  const [error, setError] = useState('')
  const betAmount = parseInt(amount)

  const handleBet = async () => {
    if (isSoundEnabled) {
      triggerHaptic()
    }

    if (!myPlayer) {
      setError('Something went wrong!')
      return
    }

    if (!biggestBet && biggestBet !== 0) {
      setError('Something went wrong!')
      return
    }

    if (!amount) {
      setError('Your bet must be bigger than 0')
      return
    }

    if (betAmount === myPlayer.chips) {
      closeSheet()
      onAllIn()
      return
    }

    const callAmount = Math.min(biggestBet - myPlayer.currentBet, myPlayer.chips)

    if (callAmount === betAmount) {
      closeSheet()
      await onCall()
      return
    }

    if ((!minimumRaise && minimumRaise !== 0) || playersOnlyCalledBB === undefined) {
      console.error('Minimum Raise or playersOnlyCalledBB missing')
      return
    }

    if (!minimumRaise && minimumRaise !== 0) {
      console.error('Minimum Raise or playersOnlyCalledBB missing')
      return
    }

    /**
     * Edge case: When going through first betting round, if every player just calls BB, but BB wants to raise,
     * BB player would just be raising their BB bet. So in that case, assume player has not bet chips yet.
     */
    const isPlayerBB = myPlayer.position === 'bb'
    const isDeal = game?.boardState === 'deal'
    //onst currentPlayerOnlyPlacedBB = false //myPlayer.bets.filter(Boolean).every((b) => b._kind === BetKind.BIGBLIND)
    const currentPlayeronlyPlacedBB = currentHandActions?.filter(
      (b) => b.boardState === game?.boardState && b.action === 'call'
    )

    let kind: ActionType
    if (biggestBet > 0) {
      kind = 'raise'
    } else {
      kind = 'bet'
    }

    const isBBRaisingThemselves = isPlayerBB && isDeal && kind === 'raise' && playersOnlyCalledBB //&& currentPlayerOnlyPlacedBB

    const playerAlreadyBetAmount = isBBRaisingThemselves ? 0 : myPlayer.currentBet
    const isValidBet = isValidBetForPlayer(
      {
        bigBlind: game?.bigBlind ?? 0,
        biggestBet: biggestBet ?? Number.POSITIVE_INFINITY,
        playerAlreadyBet: playerAlreadyBetAmount,
        playerChips: myPlayer.chips,
        // If not bb is raising themselves edge case, substract already bet amount.
        // Just for validation, actual amount is handled in api.
        betAmount: betAmount - (isBBRaisingThemselves ? 0 : myPlayer.currentBet),
        callAmount,
        minimumRaise
      },
      kind
    )

    if (!isValidBet.ok) {
      setError(
        translate(`newGameScreen.betModalError.${isValidBet.error}`, {
          betAmount,
          bigBlind: game?.bigBlind ?? 0,
          biggestBet,
          minimumRaise,
          playerBet: myPlayer.currentBet,
          kind
        })
      )
      setAmount('0')
      return
    }
    closeSheet()
    onBet(betAmount)
  }

  return (
    <Container style={[{}, props.containerStyle]}>
      <Content>
        <TurnTimer />
        {error && <ErrorMessage error={error} />}
        <BetInfo />
        <Tabs onSelect={setAmount} errorMsg={error} />
      </Content>
      <Footer>
        <Row gap={16}>
          <DS.Button variant="Large/Secondary" type="On Dark" onPress={closeSheet} maxWidth>
            Cancel
          </DS.Button>
          <DS.Button variant="Large/Primary" type={'On Light'} onPress={handleBet} maxWidth>
            Confirm
          </DS.Button>
        </Row>
      </Footer>
    </Container>
  )
}

const TABS = {
  'Suggested Bet': 'Suggested Bet',
  'Manual Entry': 'Manual Entry'
} as const

type TAB = keyof typeof TABS

interface TabsProps {
  onSelect: (amount: string) => void
  errorMsg: string
}
const Tabs = ({ onSelect, errorMsg }: TabsProps) => {
  const [selectedTab, setSelectedTab] = useState<TAB>('Suggested Bet')

  return (
    <DS.Frame justifyContent="space-between" gap={'8'}>
      <Row justifyContent="space-around">
        <TouchableOpacity
          onPress={() => {
            setSelectedTab('Suggested Bet')
            onSelect('0')
          }}
          style={[styles.tab, selectedTab === 'Suggested Bet' ? styles.selectedTab : {}]}
        >
          <DS.Text variant="Body Small/Medium">{translate('gameScreen.betModal.suggestedBet')}</DS.Text>
        </TouchableOpacity>
        <TouchableOpacity
          onPress={() => {
            setSelectedTab('Manual Entry')
            onSelect('0')
          }}
          style={[styles.tab, selectedTab === 'Manual Entry' ? styles.selectedTab : {}]}
        >
          <DS.Text variant="Body Small/Medium">{translate('gameScreen.betModal.manualEntry')}</DS.Text>
        </TouchableOpacity>
      </Row>
      <Box>
        {selectedTab === 'Suggested Bet' ? <SuggestedBet onSelect={onSelect} /> : null}
        {selectedTab === 'Manual Entry' ? <ManualEntry onSelect={onSelect} errorMsg={errorMsg} /> : null}
      </Box>
    </DS.Frame>
  )
}

const styles = StyleSheet.create({
  tabs: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    backgroundColor: DS.palette['Brand/Light Grey'],
    borderRadius: DS.spacing[32],
    padding: DS.spacing[4]
  },
  tab: {
    paddingVertical: DS.spacing[8],
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center'
  },
  selectedTab: {
    backgroundColor: 'white',
    borderRadius: DS.spacing[24],
    flex: 1
  },
  progressBarContainer: {
    height: 6,
    backgroundColor: getDSColorWithOpacity('Global/Black', 0.1),
    borderRadius: 3,
    overflow: 'hidden',
    marginBottom: 8
  },
  progressBar: {
    height: '100%',
    borderRadius: 3
  }
})

const SuggestedBet = (props: { onSelect: (amount: string) => void }) => {
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null)

  const { game, betHints, myPlayer, biggestBet, minimumRaise = 0, callAmount } = useGameState()

  const getHintsWithAllIn = (hints: BetHint[]) => {
    const allInAmount = myPlayer?.chips ?? 0

    const hintsLowerThanAllIn = hints
      .filter(
        (hint) =>
          isValidBetForPlayer(
            {
              bigBlind: game?.bigBlind ?? 0,
              biggestBet: biggestBet || 0,
              playerAlreadyBet: myPlayer?.currentBet ?? 0,
              playerChips: myPlayer?.chips ?? 0,
              betAmount: hint.amount,
              callAmount,
              minimumRaise
            },
            'bet'
          ).ok
      )
      .filter((hint) => hint.amount < allInAmount)

    const allInHint = { amount: allInAmount, kind: 'allin', label: 'All In' }
    return [...hintsLowerThanAllIn, allInHint]
  }

  const allHints = getHintsWithAllIn(betHints)
  const sortedHints = allHints.sort((a, b) => b.amount - a.amount)

  return (
    <Box gap={4}>
      {sortedHints.map((hint, index) => (
        <SuggestedBetItem
          key={`${hint.label}-${index}`}
          item={hint}
          isSelected={selectedAmount === hint.amount}
          onPress={() => {
            setSelectedAmount(hint.amount)
            props.onSelect(hint.amount.toString())
          }}
        />
      ))}
    </Box>
  )
}

const SuggestedBetItem = (props: {
  item: {
    label: string
    amount: number
  }
  onPress: () => void
  isSelected?: boolean
  containerStyle?: ViewStyle
}) => {
  return (
    <TouchableOpacity
      style={[
        {
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          height: 40,
          gap: 10,
          paddingVertical: 8,
          paddingHorizontal: 10,
          borderWidth: 2,
          borderColor: 'rgba(221, 218, 210, 1)',
          borderRadius: 4
        },
        props?.isSelected ? { borderColor: 'black' } : {},
        props?.containerStyle
      ]}
      onPress={() => props.onPress()}
    >
      <Row gap={5} alignItems="center">
        <Checkbox selected={!!props?.isSelected} />
        <DS.Text variant="Body Large/Regular" textAlign="center">
          {numberWithCommas(props.item.amount)}
        </DS.Text>
      </Row>
      <DS.Text variant="Body Small/Regular">{props.item.label}</DS.Text>
    </TouchableOpacity>
  )
}

const BetInfo = () => {
  const { biggestBet, myPlayer } = useGameState()
  const myChips = myPlayer?.chips
  return (
    <Row justifyContent="space-around">
      <Section title="Current Bet" value={biggestBet ?? 0} />
      <Section title="Your chips" value={myChips ?? 0} showChip />
    </Row>
  )
}

const TurnTimer = () => {
  const { game } = useGameState()
  if (!game) {
    throw new Error('Game not found in TurnTimer')
  }

  // Server state (from Convex subscription)
  const isPaused = game?.state === 'playing' ? Boolean(game.pausedAt) : false
  const turnDuration = game?.state === 'playing' ? game.turnDuration : 60000
  const turnStartedAt = game?.state === 'playing' ? game.turnStartedAt : null

  // Use animation frame for smooth countdown (only when not paused)
  const _now = useAnimationFrame(!isPaused)
  const now = isPaused ? Date.now() : _now

  const elapsed = turnStartedAt ? now - turnStartedAt : 0
  const timerRemaining = turnDuration - elapsed

  const progress = Math.max(0, Math.min(1, timerRemaining / turnDuration))
  const remainingSeconds = Math.ceil(timerRemaining / 1000)

  return (
    <Box>
      <Timer secondsPassed={remainingSeconds} />
      <Box marginTop={8}>
        {/* Progress Bar */}
        <View style={styles.progressBarContainer}>
          <View
            style={[styles.progressBar, { width: `${progress * 100}%`, backgroundColor: DS.palette['Brand/Orange'] }]}
          />
        </View>
      </Box>
    </Box>
  )
}

const ErrorMessage = ({ error }: { error: string }) => {
  return (
    <DS.Frame
      justifyContent={'center'}
      style={{
        width: '100%',
        alignItems: 'center',
        height: 12
      }}
    >
      {error ? (
        <DS.Text variant="Mini/Medium" color="Utility/Warning">
          {error}
        </DS.Text>
      ) : null}
    </DS.Frame>
  )
}

type TimerProps = {
  secondsPassed: number
}

const Timer = ({ secondsPassed }: TimerProps) => {
  return (
    <DS.Frame row justifyContent={'space-between'}>
      <DS.Text variant="Caption/Bold" uppercase>
        Place your bet
      </DS.Text>
      <DS.Text variant="Caption/Medium">{secondsPassed}s left</DS.Text>
    </DS.Frame>
  )
}

const Footer = createComponent(View, {
  alignSelf: 'flex-end',
  alignContent: 'flex-end',
  width: '100%',
  paddingVertical: 16,
  paddingHorizontal: 16
})

const Content = createComponent(View, {
  gap: 8,
  flexGrow: 1
})

const Container = createComponent(View, {
  flexGrow: 1,
  paddingTop: 8,
  paddingBottom: 8,
  paddingHorizontal: 16,
  height: '100%',
  _web: {
    gap: 16
  }
})
